import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    Input,
    Output,
    type OnDestroy,
    type OnInit,
} from '@angular/core';
import { FormControl, NonNullableFormBuilder, UntypedFormGroup, type UntypedFormControl } from '@angular/forms';

import { TranslateService } from '@ngx-translate/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, of, Subject, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { textImproveList, textLangList, textToneList } from '@common/constants/ai-request-text.const';
import { EAiTextType, ELanguages, type ETextImprove, type ETextLanguage, type ETextTone } from '@common/enums';
import { AiHttpService, ECodeStatus } from '../shared/service/ai.http.service';
import { imports } from './deps';
import * as langs from '../../assets/translations';
import { type AiTextPluginComponentClass } from '@sites/ai-text-plugin/src/app/shared/types/component-class.type';

export type EditorAiModalModalPayload = { generatedText: string };

@Component({
    selector: 'sp-ai-text-plugin-inner',
    templateUrl: './ai-text-plugin.component.html',
    styleUrls: ['./ai-text-plugin.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: imports,
})
export class AiTextPluginInnerComponent implements OnInit, OnDestroy, AiTextPluginComponentClass {
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly fb = inject(NonNullableFormBuilder);
    private readonly bsModalRef = inject(BsModalRef);

    @Input() public lang!: ELanguages;
    @Input() public accessToken = '';
    @Input() public text = '';
    // input for internal use only!
    @Input() public isShadowDom = false;
    @Output() public saveData: Subject<EditorAiModalModalPayload> = new Subject();
    @Output() public cancel: Subject<void> = new Subject();

    private readonly aiHttpService: AiHttpService = inject(AiHttpService);
    private readonly translate: TranslateService = inject(TranslateService);
    private readonly cookieService: CookieService = inject(CookieService);

    private isSaved = false;
    private cachedText = '';

    public isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public errorMessage = '';
    public freeKeyUsage: any = null;
    public freeKeyLimit: any = null;
    public EAiTextType = EAiTextType;
    public controlForm: UntypedFormGroup = new UntypedFormGroup({
        userPromptText: new FormControl(''),
    });

    public toggles = {
        toneDropdown: false,
        improveDropdown: false,
        langDropdown: false,
    };

    public aiTextMap: Record<EAiTextType.IMPROVE | EAiTextType.TONE | EAiTextType.LANG, any[]> = {
        [EAiTextType.IMPROVE]: textImproveList,
        [EAiTextType.TONE]: textToneList,
        [EAiTextType.LANG]: textLangList,
    };

    constructor() {
        this.initForm();
    }

    public ngOnInit(): void {
        this.cachedText = this.text;

        const shouldMerge = true;

        this.translate.setTranslation(
            this.getCurrentLang,
            langs[this.getCurrentLang as keyof typeof langs],
            shouldMerge
        );
        this.translate.use(this.getCurrentLang);
        this.changeDetectorRef.markForCheck();
    }

    public ngOnDestroy(): void {
        if (!this.isSaved) {
            this.cancel.next();
            this.bsModalRef.hide();
        }
    }

    protected initForm(): void {
        this.controlForm = this.fb.group({
            userPromptText: null,
        });
    }

    public getAiRequest(type: EAiTextType, textType?: ETextImprove | ETextTone | ETextLanguage): void {
        this.toggleDropdown();
        const requestText = this.getAiRequestText(this.text, type, textType);
        const aiPayload = [
            {
                role: 'system',
                content: 'You are a system that helps the user improve and create various texts.',
            },
        ];

        aiPayload.push({ role: 'user', content: requestText });

        if (this.text.length) {
            aiPayload.push({ role: 'user', content: this.text });
        }

        this.isLoading$.next(true);

        this.aiHttpService
            .getAiGeneratedText(aiPayload, this.accessToken)
            .pipe(
                tap(() => (this.errorMessage = '')),
                catchError((err) => {
                    if (err.error.errors) {
                        this.errorMessage = this.translate.instant('ai_free_request_error');
                        this.freeKeyUsage = 30;
                        this.freeKeyLimit = '30';
                    }

                    this.isLoading$.next(false);

                    return of(null);
                })
            )
            .subscribe((data) => {
                if (!data) {
                    this.isLoading$.next(false);
                    this.changeDetectorRef.detectChanges();
                    return;
                }

                this.freeKeyUsage = data?.isFree ? data?.freeKeyUsage : null;
                this.freeKeyLimit = data?.isFree ? data?.freeKeyLimit : null;

                if (data?.errors) {
                    switch (data?.errors?.[0]?.code) {
                        case ECodeStatus.CONTEXT_LENGTH_EXCEEDED:
                            this.errorMessage = this.translate.instant('ai_text_too_large');
                            break;
                        case ECodeStatus.INSUFFICIENT_QUOTA:
                            this.freeKeyLimit = '';
                            this.freeKeyUsage = null;
                            this.errorMessage = this.translate.instant('gpt_error_quota');
                            break;
                        default:
                            this.errorMessage = data?.errors?.[0]?.message;
                            break;
                    }
                } else {
                    this.text = data?.content;
                }

                this.isLoading$.next(false);
                this.changeDetectorRef.detectChanges();
            });
    }

    public onConfirm(): void {
        this.isSaved = true;
        this.saveData.next({ generatedText: this.text });
        this.bsModalRef.hide();
    }

    public onCancel(): void {
        this.cancel.next();
        this.bsModalRef.hide();
        this.text = this.cachedText;
    }

    private getAiRequestText(
        text: string,
        type: EAiTextType,
        textType?: ETextImprove | ETextTone | ETextLanguage
    ): string {
        let requestText = ``;

        switch (type) {
            case EAiTextType.TONE:
            case EAiTextType.IMPROVE: {
                const task = this.aiTextMap[type].find((el) => el.label === textType).value;
                requestText += `Make previous text ${task} using the language in which it written`;
                return requestText;
            }
            case EAiTextType.LANG:
                requestText = this.aiTextMap[type].find((el) => el.label === textType).value;
                return requestText;
            case EAiTextType.REPEAT:
                requestText += 'Show another version of previous text using the language in which it written';
                return requestText;
            case EAiTextType.CUSTOM:
                requestText += this.userPromptText.value;
                return requestText;
            default:
                return requestText;
        }
    }

    public get userPromptText(): UntypedFormControl {
        return this.controlForm.get('userPromptText') as UntypedFormControl;
    }

    public onCreateNewText() {
        if (this.text.length) {
            this.text = '';
        } else {
            this.getAiRequest(EAiTextType.CUSTOM);
        }
    }

    public toggleDropdown(dropdownVar?: keyof typeof this.toggles) {
        // if dropdownVar is undefined, close all dropdowns
        // for click outside

        if (!dropdownVar) {
            this.toggles = {
                toneDropdown: false,
                improveDropdown: false,
                langDropdown: false,
            };
            this.changeDetectorRef.detectChanges();
            return;
        }

        (Object.keys(this.toggles) as (keyof typeof this.toggles)[]).forEach((key) => {
            if (key !== dropdownVar) {
                return (this.toggles[key] = false);
            }
            return (this.toggles[key] = !this.toggles[key]);
        });
        this.changeDetectorRef.detectChanges();
    }

    public get getCurrentLang(): ELanguages {
        const langCookie: string = this.cookieService.get('lang').replace('-', '_');
        return this.lang || ((langCookie ? langCookie.split(':')[0] : ELanguages.EN) as ELanguages);
    }
}
